<template>
  <div class="sending-policies-page" dir="rtl">
    <h1 class="page-title">מדיניות שליחה</h1>
    <p class="page-subtitle">
      הנחיות לשליחת מועמדים — ברמת החברה (חלות על כל המשרות שלה) או ברמת משרה בודדת.
      לכל כלל יש סיבה וראיות שאפשר לפתוח ולקרוא.
    </p>

    <!-- Loading -->
    <div v-if="is_loading" class="loading-wrap">
      <div class="loading"></div>
    </div>

    <!-- COMPANY LIST -->
    <template v-else-if="!selected">
      <div class="search-row">
        <input
          v-model="search"
          class="search-input"
          type="text"
          placeholder="חיפוש חברה לפי שם"
        />
        <span class="search-count">{{ filteredCompanies.length }} חברות עם כללים</span>
      </div>

      <table v-if="filteredCompanies.length" class="policies-table">
        <thead>
          <tr>
            <th>חברה</th>
            <th class="col-num">כללי חברה</th>
            <th class="col-num">כללי משרות</th>
            <th class="col-num">פעילים</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="company in filteredCompanies"
            :key="company.company_id"
            class="company-row"
            @click="openCompany(company.company_id)"
          >
            <td class="company-name">{{ company.company_name }}</td>
            <td class="col-num">{{ company.company_rule_count }}</td>
            <td class="col-num">{{ company.position_rule_count }}</td>
            <td class="col-num">{{ company.active_rule_count }}</td>
          </tr>
        </tbody>
      </table>

      <p v-else-if="companies.length" class="no-results">לא נמצאה חברה בשם הזה</p>
      <p v-else class="no-results">עדיין לא הוגדרו כללי מדיניות שליחה</p>
    </template>

    <!-- ONE COMPANY -->
    <template v-else>
      <div class="company-header">
        <button class="back-btn" @click="backToList">→ חזרה לרשימת החברות</button>
        <h2 class="company-title">{{ selected.company_name }}</h2>
      </div>

      <!-- Company-wide rules -->
      <section class="rules-section">
        <div class="section-head">
          <h3 class="section-title">כללים ברמת החברה</h3>
          <button class="add-btn" @click="openAddForm('Company', selected.company_id)">+ כלל חדש</button>
        </div>

        <div v-if="isAdding('Company', selected.company_id)" class="add-form">
          <textarea v-model="new_rule.instruction" class="add-textarea" rows="3" placeholder="ההנחיה למגייסת"></textarea>
          <textarea v-model="new_rule.reason" class="add-textarea" rows="2" placeholder="הסיבה לכלל"></textarea>
          <div class="add-actions">
            <select v-model="new_rule.confidence" class="add-select">
              <option value="high">ודאות גבוהה</option>
              <option value="medium">ודאות בינונית</option>
            </select>
            <label class="add-check">
              <input v-model="new_rule.requires_human" type="checkbox" />
              דורש בדיקת מגייסת
            </label>
            <button class="save-btn" :disabled="is_creating || !new_rule.instruction.trim()" @click="createRule">
              {{ is_creating ? '...שומר' : 'הוספה' }}
            </button>
            <button class="cancel-btn" :disabled="is_creating" @click="closeAddForm">ביטול</button>
          </div>
        </div>

        <SendingPolicyRuleCard
          v-for="rule in selected.company_rules"
          :key="rule.id"
          :rule="rule"
          @updated="onRuleUpdated"
        />
        <p v-if="!selected.company_rules.length" class="empty-hint">אין כללים ברמת החברה</p>
      </section>

      <!-- Per-position rules -->
      <section
        v-for="group in selected.position_rules"
        :key="group.position_id"
        class="rules-section"
      >
        <div class="section-head">
          <h3 class="section-title">
            משרה: {{ group.position_title || ('#' + group.position_id) }}
            <span class="position-id">#{{ group.position_id }}</span>
          </h3>
          <button class="add-btn" @click="openAddForm('Position', group.position_id)">+ כלל חדש</button>
        </div>

        <div v-if="isAdding('Position', group.position_id)" class="add-form">
          <textarea v-model="new_rule.instruction" class="add-textarea" rows="3" placeholder="ההנחיה למגייסת"></textarea>
          <textarea v-model="new_rule.reason" class="add-textarea" rows="2" placeholder="הסיבה לכלל"></textarea>
          <div class="add-actions">
            <select v-model="new_rule.confidence" class="add-select">
              <option value="high">ודאות גבוהה</option>
              <option value="medium">ודאות בינונית</option>
            </select>
            <label class="add-check">
              <input v-model="new_rule.requires_human" type="checkbox" />
              דורש בדיקת מגייסת
            </label>
            <button class="save-btn" :disabled="is_creating || !new_rule.instruction.trim()" @click="createRule">
              {{ is_creating ? '...שומר' : 'הוספה' }}
            </button>
            <button class="cancel-btn" :disabled="is_creating" @click="closeAddForm">ביטול</button>
          </div>
        </div>

        <SendingPolicyRuleCard
          v-for="rule in group.rules"
          :key="rule.id"
          :rule="rule"
          @updated="onRuleUpdated"
        />
      </section>

      <p v-if="!selected.position_rules.length" class="empty-hint">אין כללים ברמת משרה בחברה זו</p>
    </template>
  </div>
</template>

<script>
import SendingPolicyRuleCard from '@/components/SendingPolicyRuleCard'

export default {
  name: 'sending-policies-page',
  components: {
    SendingPolicyRuleCard
  },
  data () {
    return {
      companies: [],
      selected: null,
      search: '',
      is_loading: false,
      is_creating: false,
      // {resource_type, resource_id} of the section whose "new rule" form is open
      add_target: null,
      new_rule: this.blankRule()
    }
  },
  computed: {
    filteredCompanies () {
      const term = this.search.trim()
      if (!term) {
        return this.companies
      }
      return this.companies.filter(c => String(c.company_name || '').includes(term))
    }
  },
  mounted () {
    // Deep link support: /sending-policies?company_id=123 opens that company directly.
    const companyId = this.$route.query.company_id
    if (companyId) {
      this.openCompany(companyId)
    } else {
      this.loadCompanies()
    }
  },
  methods: {
    blankRule () {
      return { instruction: '', reason: '', confidence: 'high', requires_human: false }
    },
    loadCompanies () {
      this.is_loading = true
      this.$network_utils
        .getSendingPolicies({})
        .then(res => {
          this.companies = (res && res.companies) || []
        })
        .catch(() => {
          this.$utils.emit('show_toast', { text: 'שגיאה בטעינת רשימת החברות', type: 'error' })
        })
        .finally(() => {
          this.is_loading = false
        })
    },
    openCompany (companyId) {
      this.is_loading = true
      this.closeAddForm()
      this.$network_utils
        .getSendingPolicies({ company_id: companyId })
        .then(res => {
          this.selected = res || null
          if (String(this.$route.query.company_id || '') !== String(companyId)) {
            this.$router.replace({ name: 'sending-policies', query: { company_id: String(companyId) } })
          }
        })
        .catch(error => {
          const slug = error && error.response && error.response.data && error.response.data.error
          const text = slug === 'company_not_found' ? 'החברה לא נמצאה' : 'שגיאה בטעינת כללי החברה'
          this.$utils.emit('show_toast', { text: text, type: 'error' })
          this.selected = null
          if (!this.companies.length) {
            this.loadCompanies()
          }
        })
        .finally(() => {
          this.is_loading = false
        })
    },
    backToList () {
      this.selected = null
      this.closeAddForm()
      this.$router.replace({ name: 'sending-policies' })
      this.loadCompanies()
    },
    isAdding (resourceType, resourceId) {
      return !!this.add_target &&
        this.add_target.resource_type === resourceType &&
        String(this.add_target.resource_id) === String(resourceId)
    },
    openAddForm (resourceType, resourceId) {
      this.add_target = { resource_type: resourceType, resource_id: resourceId }
      this.new_rule = this.blankRule()
    },
    closeAddForm () {
      this.add_target = null
      this.new_rule = this.blankRule()
    },
    createRule () {
      if (!this.add_target || this.is_creating) {
        return
      }
      const instruction = this.new_rule.instruction.trim()
      if (!instruction) {
        return
      }
      this.is_creating = true
      this.$network_utils
        .createSendingPolicy({
          resource_type: this.add_target.resource_type,
          resource_id: this.add_target.resource_id,
          instruction: instruction,
          reason: this.new_rule.reason.trim(),
          confidence: this.new_rule.confidence,
          requires_human: this.new_rule.requires_human
        })
        .then(() => {
          this.$utils.emit('show_toast', { text: 'הכלל נוסף' })
          this.closeAddForm()
          // Reload the company so the new rule lands in the right group with the
          // server's own ordering instead of being spliced in optimistically.
          this.openCompany(this.selected.company_id)
        })
        .catch(() => {
          this.$utils.emit('show_toast', { text: 'שגיאה בהוספת הכלל — נסי שוב', type: 'error' })
        })
        .finally(() => {
          this.is_creating = false
        })
    },
    // Replace the edited rule in place — no full reload, so the recruiter keeps
    // her scroll position while working through a long company.
    onRuleUpdated (updated) {
      if (!updated || !this.selected) {
        return
      }
      const replaceIn = list => {
        const idx = list.findIndex(r => r.id === updated.id)
        if (idx !== -1) {
          this.$set(list, idx, updated)
          return true
        }
        return false
      }
      if (replaceIn(this.selected.company_rules)) {
        return
      }
      this.selected.position_rules.some(group => replaceIn(group.rules))
    }
  }
}
</script>

<style scoped>
.sending-policies-page {
  padding: 20px;
  text-align: right;
}
.page-title {
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 6px;
  color: #2c3e50;
}
.page-subtitle {
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 18px;
  line-height: 1.6;
}
.search-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
  flex-wrap: wrap;
}
.search-input {
  min-width: 260px;
  border: 1px solid #cbd5e0;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 14px;
  text-align: right;
  font-family: inherit;
}
.search-count {
  font-size: 13px;
  color: #6b7280;
}
.loading-wrap {
  display: flex;
  justify-content: center;
  padding: 40px 0;
}
.policies-table {
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}
.policies-table th,
.policies-table td {
  padding: 10px 12px;
  font-size: 14px;
  text-align: right;
}
.policies-table tr {
  border-bottom: 2px solid #cbd5e0;
}
.policies-table th {
  background: #f1f5f9;
  font-weight: 700;
  color: #2c3e50;
}
.company-row {
  cursor: pointer;
}
.company-row:hover {
  background: #f5f6fe;
}
.company-name {
  font-weight: 700;
  color: #3273dc;
}
.col-num {
  width: 120px;
  text-align: center;
}
.company-header {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 18px;
  flex-wrap: wrap;
}
.back-btn {
  background: #fff;
  border: 1px solid #cbd5e0;
  border-radius: 8px;
  padding: 6px 14px;
  font-size: 13px;
  color: #475569;
  cursor: pointer;
}
.back-btn:hover {
  background: #f1f5f9;
}
.company-title {
  font-size: 20px;
  font-weight: 700;
  color: #2c3e50;
}
.rules-section {
  margin-bottom: 24px;
}
.section-head {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
}
.section-title {
  font-size: 16px;
  font-weight: 700;
  color: #334155;
}
.position-id {
  font-size: 12px;
  color: #94a3b8;
  direction: ltr;
}
.add-btn {
  background: none;
  border: 1px dashed #94a3b8;
  border-radius: 8px;
  color: #475569;
  font-size: 12px;
  padding: 4px 12px;
  cursor: pointer;
}
.add-btn:hover {
  background: #f1f5f9;
}
.add-form {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 12px;
}
.add-textarea {
  width: 100%;
  border: 1px solid #cbd5e0;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 14px;
  font-family: inherit;
  text-align: right;
  resize: vertical;
  margin-bottom: 6px;
}
.add-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.add-select {
  border: 1px solid #cbd5e0;
  border-radius: 6px;
  padding: 5px 10px;
  font-size: 13px;
  font-family: inherit;
}
.add-check {
  font-size: 13px;
  color: #475569;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}
.save-btn {
  background: #25a244;
  border: 1px solid #25a244;
  color: #fff;
  border-radius: 6px;
  padding: 6px 18px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}
.save-btn:disabled {
  opacity: 0.5;
  cursor: default;
}
.cancel-btn {
  background: #fff;
  border: 1px solid #cbd5e0;
  color: #475569;
  border-radius: 6px;
  padding: 6px 18px;
  font-size: 13px;
  cursor: pointer;
}
.empty-hint {
  font-size: 13px;
  color: #94a3b8;
  padding: 6px 0 10px;
}
.no-results {
  padding: 30px;
  text-align: center;
  color: #7f8c8d;
}
</style>
